import { bottomLevelId } from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import { FloorAssemblyType, type FloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import { resolveBayFramingField } from "building-types/floors/floorBayResolution";
import { partitionFloorBoundaries } from "building-types/floors/floorBayUtils";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type { FrameFloorAssemblyEntity } from "building-types/floors/frameFloorAssemblyTypes";
import type { SlabFloorAssemblyEntity } from "building-types/floors/slabFloorAssemblyTypes";
import { FloorFramingComponentType } from "building-types/framing/floorFramingComponentTypes";
import type { Framing } from "building-types/framing/floorFramingTypes";
import type { FloorBayHandle } from "building-types/handles/docEntityHandleTypes";
import type { Formula } from "building-types/options/formula";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { defaultFrameFloorAssemblyId, defaultSlabFloorAssemblyId } from "doc/defaults/defaultFloorAssembly";
import { getFloorSplitData, isVoidFloorAssemblyOverride } from "doc/elements/floor/floorAssembly";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { poleOfInaccessibilityPolygon } from "geom/label";
import { polyLine2AABB2 } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3ToPolyLine2IgnoreZ } from "geom/polyLine3";
import type { Label } from "lib/house/plans/label";
import { labelNew, tagFontSize } from "lib/house/plans/label";
import { TAG_FONT_SIZE } from "pdf/planFonts";
import { makeSheetGeometryFromLabels } from "./planLabels";
import { planTagCoordinatesByLine } from "./planTags";

function resolveAssemblyEntity(
  doc: Doc,
  floor: FloorAssemblyOverride,
  floorSystem: { assemblyId?: string; structuralType?: StructuralFloorType; disabled?: boolean | Formula },
  bayAssemblyIdOverride?: string,
): FrameFloorAssemblyEntity | SlabFloorAssemblyEntity | undefined {
  if (floorSystem.disabled) {
    return undefined;
  }
  const structuralType =
    floor.assemblyType === FloorAssemblyType.VOID
      ? undefined
      : ("structuralType" in floor ? floor.structuralType : undefined) ??
        floorSystem.structuralType ??
        StructuralFloorType.FRAMING;

  const defaultAsm =
    structuralType === StructuralFloorType.SLAB ? defaultSlabFloorAssemblyId : defaultFrameFloorAssemblyId;
  const assemblyId =
    bayAssemblyIdOverride ??
    ("assemblyId" in floor ? floor.assemblyId : undefined) ??
    floorSystem.assemblyId ??
    defaultAsm;

  return doc.frameFloorAssemblies[assemblyId] ?? doc.slabFloorAssemblies[assemblyId];
}

function textRotationFromJoistSpan(framingForBay: Framing[] | undefined): number {
  if (!framingForBay?.length) {
    return 0;
  }
  for (const member of framingForBay) {
    if (member.type === FloorFramingComponentType.FLOOR_JOIST && member.rails.length > 0) {
      const pl2 = polyLine3ToPolyLine2IgnoreZ(member.rails[0].rail);
      if (pl2.length >= 4) {
        return planTagCoordinatesByLine(pl2).angle;
      }
    }
  }
  for (const member of framingForBay) {
    if (member.type === FloorFramingComponentType.RIM_JOIST && member.rails.length > 0) {
      const pl2 = polyLine3ToPolyLine2IgnoreZ(member.rails[0].rail);
      if (pl2.length >= 4) {
        return planTagCoordinatesByLine(pl2).angle;
      }
    }
  }
  return 0;
}

/** Fallback when no joist geometry exists (e.g. slab): align text to the longer side of the bay AABB. */
function textRotationForSlabBay(perimeter: PolyLine2): number {
  const aabb = polyLine2AABB2(perimeter);
  const w = aabb.maxX - aabb.minX;
  const h = aabb.maxY - aabb.minY;
  if (w >= h) {
    return 0;
  }
  return Math.PI / 2;
}

export function makeFloorSystemAssemblyTagLabels(
  doc: Doc,
  building: Building,
  levelId: string,
  noSettings: boolean | undefined,
): Label[] {
  const labels: Label[] = [];
  let fontSize: SheetInches | undefined;
  if (!noSettings) {
    fontSize = tagFontSize(building.settings.planset.paperSize);
  }

  const { baysByFloorId } = partitionFloorBoundaries(building.floorBoundaries);

  for (const [floorId, bays] of baysByFloorId) {
    const floor = building.horizontalAssemblyFloors[floorId];
    if (!floor || floor.disabled || isVoidFloorAssemblyOverride(floor)) {
      continue;
    }
    const parentId = floor.parentId;
    if (typeof parentId !== "string") {
      continue;
    }
    const floorSystem = building.horizontalAssemblyFloorSystems[parentId];
    if (!floorSystem) {
      continue;
    }

    const splitData = getFloorSplitData(floor);

    for (const { bayId, boundary } of bays) {
      const framedAsm = building.framedFloorAssemblies[bayId];
      const effectiveLevelId = framedAsm?.levelId ?? floor.levelId ?? bottomLevelId(building.levels);
      if (effectiveLevelId !== levelId) {
        continue;
      }

      const bayDef = splitData?.bays?.[bayId];
      const bayAssemblyId = resolveBayFramingField(bayDef, floorSystem.assemblyId, "assemblyId");
      const assemblyEntity = resolveAssemblyEntity(doc, floor, floorSystem, bayAssemblyId);

      const tagText = assemblyEntity?.tag?.trim() || undefined;
      if (!tagText) {
        continue;
      }

      const perimeters = boundary.perimeters;
      if (!perimeters?.length) {
        continue;
      }

      const openings = boundary.openings ?? [];
      for (let perimeterIndex = 0; perimeterIndex < perimeters.length; perimeterIndex++) {
        const perimeter = perimeters[perimeterIndex];
        const centroid = poleOfInaccessibilityPolygon([perimeter, ...openings]);
        if (!centroid) {
          continue;
        }
        const [cx, cy] = centroid;
        if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
          continue;
        }

        const textRotationRads = framedAsm?.framing[perimeterIndex]?.length
          ? textRotationFromJoistSpan(framedAsm.framing[perimeterIndex])
          : textRotationForSlabBay(perimeter);

        const handle: FloorBayHandle = {
          type: "FLOOR_BAY",
          id: bayId,
        };

        labels.push(
          labelNew({
            text: tagText,
            fontSize: fontSize ?? (TAG_FONT_SIZE as SheetInches),
            centerX: cx,
            centerY: cy,
            textRotationRads,
            handle,
          }),
        );
      }
    }
  }

  return labels;
}

export function makeSheetGeometryFromFloorSystemTags(ctx: PlanGenerationContext): SheetGeometry[] {
  const { building, levelId, noSettings } = ctx;
  const doc = building.doc;
  const labels = makeFloorSystemAssemblyTagLabels(doc, building, levelId, noSettings);
  if (labels.length === 0) {
    return [];
  }
  return makeSheetGeometryFromLabels({
    handle: building.levels[levelId].handle,
    labels,
    noSettings,
  });
}
